import { useEffect, useMemo, useState } from 'react';
import { Banknote, CreditCard, Wallet, X } from 'lucide-react';
import Button from '../../components/Button';
import { formatCurrency } from '../../data/mockData';
import styles from './PosAmendSettlementModal.module.css';

const METHODS = [
  { key: 'CASH', label: 'Cash', icon: Banknote },
  { key: 'UPI', label: 'UPI', icon: Wallet },
  { key: 'CARD', label: 'Card', icon: CreditCard },
];

function normalizeSettlement(settlement, { payLater = false } = {}) {
  const direction = String(settlement?.direction || '').toUpperCase();
  const amount = settlement?.amount;
  if (direction === 'DUE' || direction === 'REFUND') {
    return {
      direction,
      amount,
      title: direction === 'DUE'
        ? (payLater
          ? `Outstanding increases by ${formatCurrency(Number(amount) || 0)}`
          : `Collect ${formatCurrency(Number(amount) || 0)}`)
        : (payLater
          ? `Outstanding decreases by ${formatCurrency(Number(amount) || 0)}`
          : `Refund ${formatCurrency(Number(amount) || 0)}`),
      hint: payLater
        ? (direction === 'DUE'
          ? 'Pay Later bill — amount still owed goes up. No drawer tender on amend.'
          : 'Pay Later bill — amount still owed goes down. No drawer tender on amend.')
        : (direction === 'DUE'
          ? 'Customer owes the difference. Pick how they pay, then confirm.'
          : 'Refund the difference. Prefer Cash at the counter; UPI refunds are an ops process.'),
      needsMethod: !payLater,
      preferCash: !payLater && direction === 'REFUND',
      ready: amount != null && Number.isFinite(Number(amount)),
    };
  }
  if (direction === 'EVEN') {
    return {
      direction: 'EVEN',
      amount: amount != null && Number.isFinite(Number(amount)) ? Number(amount) : 0,
      title: 'No change in total',
      hint: payLater
        ? 'Totals match. Confirm to save the Pay Later bill.'
        : 'Totals match. Confirm to save — change Cash / UPI / Card if the customer switched how they pay.',
      needsMethod: !payLater,
      preferCash: false,
      ready: true,
    };
  }
  return {
    direction: '',
    amount: null,
    title: 'Waiting for preview…',
    hint: 'Confirm stays disabled until the server returns settlement.',
    needsMethod: false,
    preferCash: false,
    ready: false,
  };
}

export default function PosAmendSettlementModal({
  preview,
  preferredMethod = '',
  confirming = false,
  repreviewing = false,
  confirmError = '',
  onClose,
  onConfirm,
}) {
  const isPayLater = String(preferredMethod || '').toUpperCase() === 'PAY_LATER';
  const copy = useMemo(
    () => normalizeSettlement(preview?.settlement, { payLater: isPayLater }),
    [preview, isPayLater],
  );
  const preferred = String(preferredMethod || '').toUpperCase();
  const initialMethod = METHODS.some((m) => m.key === preferred)
    ? preferred
    : (copy.preferCash ? 'CASH' : '');
  const [method, setMethod] = useState(initialMethod);

  useEffect(() => {
    if (isPayLater) {
      setMethod('');
      return;
    }
    const nextPreferred = String(preferredMethod || '').toUpperCase();
    if (METHODS.some((m) => m.key === nextPreferred)) {
      setMethod(nextPreferred);
      return;
    }
    setMethod(copy.preferCash ? 'CASH' : copy.needsMethod ? '' : '');
  }, [copy.direction, copy.preferCash, copy.needsMethod, preferredMethod, isPayLater, preview?.original_total, preview?.new_total]);

  const busy = confirming || repreviewing;
  const canConfirm = copy.ready && !busy && (!copy.needsMethod || Boolean(method));
  const revision = preview?.receipt_preview?.receipt_revision
    ?? preview?.receipt_revision
    ?? null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="pos-amend-title">
      <div className={styles.modal}>
        <button type="button" className={styles.closeBtn} onClick={onClose} disabled={busy} aria-label="Close">
          <X size={16} />
        </button>
        <h2 id="pos-amend-title" className={styles.title}>Settlement</h2>
        <p className={styles.subtitle}>{copy.title}</p>
        <p className={styles.hint}>{copy.hint}</p>
        {revision != null && (
          <p className={styles.revisionHint}>Will print as revision {revision}</p>
        )}

        <div className={styles.totals}>
          <div className={styles.row}>
            <span>Original</span>
            <span>{formatCurrency(preview?.original_total ?? 0)}</span>
          </div>
          <div className={styles.row}>
            <span>New total</span>
            <span>{formatCurrency(preview?.new_total ?? 0)}</span>
          </div>
          {(Number(preview?.new_round_off) || 0) !== 0 && (
            <div className={styles.row}>
              <span>Round off</span>
              <span>
                {(Number(preview.new_round_off) > 0 ? '+' : '')
                  + formatCurrency(Number(preview.new_round_off))}
              </span>
            </div>
          )}
          {(preview?.amount_paid != null || preview?.settlement?.amount_paid != null) && (
            <div className={styles.row}>
              <span>Already collected</span>
              <span>{formatCurrency(Number(preview.amount_paid ?? preview.settlement?.amount_paid) || 0)}</span>
            </div>
          )}
          {(preview?.new_outstanding != null || preview?.settlement?.new_outstanding != null) && (
            <div className={styles.row}>
              <span>New outstanding</span>
              <span>{formatCurrency(Number(preview.new_outstanding ?? preview.settlement?.new_outstanding) || 0)}</span>
            </div>
          )}
          <div className={`${styles.row} ${styles.rowEmphasis}`}>
            <span>
              {isPayLater && (copy.direction === 'DUE' || copy.direction === 'REFUND')
                ? 'Outstanding change'
                : copy.direction === 'DUE'
                  ? 'Collect'
                  : copy.direction === 'REFUND'
                    ? 'Refund'
                    : 'Settlement'}
            </span>
            <span>{formatCurrency(Number(copy.amount) || 0)}</span>
          </div>
        </div>

        {copy.needsMethod && (
          <div className={styles.methods}>
            <p className={styles.methodsLabel}>Payment method</p>
            {METHODS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                className={`${styles.methodBtn} ${method === key ? styles.methodActive : ''} ${copy.preferCash && key === 'CASH' ? styles.methodPreferred : ''}`}
                onClick={() => setMethod(key)}
                disabled={busy}
              >
                <Icon size={16} /> {label}{copy.preferCash && key === 'CASH' ? ' · prefer' : ''}
              </button>
            ))}
          </div>
        )}

        {confirmError && <p className={styles.error}>{confirmError}</p>}
        {repreviewing && <p className={styles.repreview}>Refreshing preview…</p>}

        <div className={styles.actions}>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Back
          </Button>
          <Button
            disabled={!canConfirm}
            onClick={() => onConfirm({
              // Method always sent so EVEN amends can update tender (UPI → Cash, etc.).
              payment_method: copy.needsMethod ? method : undefined,
            })}
          >
            {confirming ? 'Amending…' : repreviewing ? 'Refreshing…' : 'Confirm amend'}
          </Button>
        </div>
      </div>
    </div>
  );
}
